/**
 * Collects the disabling conditions for the Business Office notes header
 * buttons ("Save BO Notes" and "Trigger RPA") into human-readable messages.
 *
 * The header already receives the blocker booleans; this hook only maps them
 * to copy so the disabled state and the button tooltips always agree.
 *
 * @module useBoNotesValidationMessages
 */
import { useMemo } from "react";
import { BoNotesValidationMessages } from "../../../../../constants/boNotesValidationMessages";

/**
 * The individual RPA blockers, as computed by {@link useBoActionState}.
 */
export interface RpaBlockers {
  /** An RPA run is already in progress. */
  isRpaInProgress: boolean;
  /** RPA has already been triggered for this order. */
  isRpaAlreadyTriggered: boolean;
  /** Any CPT row has an invalid value on a save-blocking field. */
  hasCptTableAnyError: boolean;
  /** No business office notes exist yet. */
  hasNoBoNotes: boolean;
  /** No final report comments exist. */
  hasNoFinalReportComments: boolean;
  /** Not every final report comment has a BO status selected. */
  hasBoStatusMissing: boolean;
  /** EV write-back is in progress or queued. */
  isEvWriteBackInProgress: boolean;
  /** No J-codes are present on the order. */
  hasNoJCodes: boolean;
}

interface UseBoNotesValidationMessagesParams {
  /** The individual RPA blockers. */
  rpaBlockers: RpaBlockers;
  /** Whether a BO-notes save request is in flight. */
  saveBoLoading: boolean;
}

export interface BoNotesValidationMessagesResult {
  /** Messages for the "Save BO Notes" button tooltip. */
  saveBoMessages: string[];
  /** True when the "Save BO Notes" button should be disabled. */
  isSaveBoDisabled: boolean;
  /** Messages for the "Trigger RPA" button tooltip. */
  triggerRpaMessages: string[];
  /** True when the "Trigger RPA" button should be disabled. */
  isTriggerRpaDisabled: boolean;
}

/**
 * Maps the Business Office header blockers to tooltip messages.
 *
 * @returns Per-button message lists plus the combined disabled flags.
 */
export const useBoNotesValidationMessages = ({
  rpaBlockers,
  saveBoLoading,
}: UseBoNotesValidationMessagesParams): BoNotesValidationMessagesResult => {
  const {
    isRpaInProgress,
    isRpaAlreadyTriggered,
    hasCptTableAnyError,
    hasNoBoNotes,
    hasNoFinalReportComments,
    hasBoStatusMissing,
    isEvWriteBackInProgress,
    hasNoJCodes,
  } = rpaBlockers;

  const triggerRpaMessages = useMemo(() => {
    const messages: string[] = [];
    if (isRpaInProgress) {
      messages.push(BoNotesValidationMessages.RPA_IN_PROGRESS);
    }
    if (isRpaAlreadyTriggered) {
      messages.push(BoNotesValidationMessages.RPA_ALREADY_TRIGGERED);
    }
    if (hasCptTableAnyError) {
      messages.push(BoNotesValidationMessages.CPT_TABLE_HAS_ERRORS);
    }
    if (hasNoBoNotes) {
      messages.push(BoNotesValidationMessages.NO_BO_NOTES);
    }
    if (hasNoFinalReportComments) {
      messages.push(BoNotesValidationMessages.NO_FINAL_REPORT_COMMENTS);
    }
    if (hasBoStatusMissing) {
      messages.push(BoNotesValidationMessages.BO_STATUS_MISSING);
    }
    if (isEvWriteBackInProgress) {
      messages.push(BoNotesValidationMessages.EV_WRITE_BACK_IN_PROGRESS);
    }
    if (hasNoJCodes) {
      messages.push(BoNotesValidationMessages.NO_J_CODES);
    }
    return messages;
  }, [
    isRpaInProgress,
    isRpaAlreadyTriggered,
    hasCptTableAnyError,
    hasNoBoNotes,
    hasNoFinalReportComments,
    hasBoStatusMissing,
    isEvWriteBackInProgress,
    hasNoJCodes,
  ]);

  const saveBoMessages = useMemo(() => {
    const messages: string[] = [...triggerRpaMessages];
    if (saveBoLoading) {
      messages.push(BoNotesValidationMessages.SAVE_BO_IN_PROGRESS);
    }
    return messages;
  }, [triggerRpaMessages, saveBoLoading]);

  return {
    saveBoMessages,
    isSaveBoDisabled: saveBoMessages.length > 0,
    triggerRpaMessages,
    isTriggerRpaDisabled: triggerRpaMessages.length > 0,
  };
};
